import { PopoverHeading, Status, StatusPopover, Tag, formatDateTime, type StatusTone, type TagTone } from "@amber/ui";
import type { ItemRow, ItemStatus, StatusView } from "../../contracts/index";

export const STATUS_TONE: Record<ItemStatus, StatusTone> = {
  new: "invited",
  in_progress: "running",
  waiting: "waiting",
  failed: "attention",
  finished: "ok",
  excluded: "off",
};

const TAG_TONE: Record<ItemStatus, TagTone> = {
  new: "accent",
  in_progress: "accent",
  waiting: "waiting",
  failed: "attention",
  finished: "strong",
  excluded: "quiet",
};

export function StatusBadge({ status }: { status: StatusView }) {
  return <Tag tone={TAG_TONE[status.status]}>{status.label}</Tag>;
}

function hasPopover(s: StatusView): boolean {
  return (
    s.status === "failed" ||
    s.status === "waiting" ||
    s.status === "excluded" ||
    s.reason !== null ||
    s.precise === "retrying" ||
    s.precise === "manual_retry"
  );
}

export function StatusCell({ status }: { status: StatusView }) {
  const label = (
    <Status tone={STATUS_TONE[status.status]}>
      {status.status === "failed" ? <span className="font-strong text-attention">{status.label}</span> : status.label}
    </Status>
  );
  if (!hasPopover(status)) return <span title={status.precise_label}>{label}</span>;
  return (
    <StatusPopover
      label={<span className="underline decoration-dotted underline-offset-2">{label}</span>}
      accessibleLabel={`${status.label}, ${status.precise_label}, details`}
    >
      <PopoverHeading>{status.title}</PopoverHeading>
      {status.reason ? <p>{status.reason}</p> : null}
      <dl>
        <dt>State</dt>
        <dd>{status.precise_label}</dd>
        {status.attempts ? (
          <>
            <dt>Attempts</dt>
            <dd>{`${String(status.attempts.used)} of ${String(status.attempts.allowed)}`}</dd>
          </>
        ) : null}
        {status.stopped ? (
          <>
            <dt>Stopped</dt>
            <dd>
              {status.stopped.by}, {formatDateTime(status.stopped.at)}
            </dd>
          </>
        ) : null}
        {status.next ? (
          <>
            <dt>Next</dt>
            <dd>
              {status.next}
              {status.next_at ? `, ${formatDateTime(status.next_at)}` : ""}
            </dd>
          </>
        ) : null}
      </dl>
    </StatusPopover>
  );
}

export function ReadinessCell({ readiness }: { readiness: ItemRow["readiness"] }) {
  if (!readiness) return <span className="text-muted">—</span>;
  const label = (
    <span className={readiness.result === "eligible" ? "font-strong text-done" : "text-fg"}>{readiness.label}</span>
  );
  return (
    <StatusPopover label={label} accessibleLabel={`Readiness: ${readiness.label}, details`}>
      <PopoverHeading>{readiness.result === "eligible" ? "Eligible" : "Draft"}</PopoverHeading>
      {readiness.reason ? <p>{readiness.reason}</p> : null}
      <ul className="m-0 list-none p-0 text-md">
        {readiness.people.map((p) => (
          <li key={`${String(p.communication)}-${p.name}`}>
            {p.result === "eligible" ? "Eligible" : "Draft"} for {p.name}
            {p.reason ? <span className="text-muted">: {p.reason}</span> : null}
            {readiness.communications > 1 ? (
              <span className="text-muted"> · communication {p.communication}</span>
            ) : null}
          </li>
        ))}
      </ul>
      <p className="mt-1.5 text-md text-muted">Assessed per tracked person · {readiness.policy}</p>
    </StatusPopover>
  );
}
